<template>
  <div class="publisher-selector">
    <v-autocomplete
      v-model="selectedPublisher"
      :items="publisherResults"
      :loading="isSearching || isUpdating"
      :search-input.sync="publisherSearch"
      hide-no-data
      hide-selected
      item-text="name"
      item-value="id"
      label="Search publishers"
      placeholder="Start typing to search"
      prepend-icon="mdi-magnify"
      return-object
      clearable
      @change="updateBookPublisher"
    >
      <template #item="{ item }">
        <v-list-item-content>
          <v-list-item-title v-text="item.name" />
          <v-list-item-subtitle v-if="item.description">
            {{ item.description }}
          </v-list-item-subtitle>
        </v-list-item-content>
      </template>
    </v-autocomplete>

    <v-card
      v-if="currentPublisher"
      outlined
      class="mt-4"
      :class="{ 'green lighten-4': showSuccess }"
    >
      <v-card-title class="subtitle-1">
        {{
          $t(
            "admin.productsDatabase.importWizard.steps.publicationDetails.currentPublisher"
          )
        }}
      </v-card-title>
      <v-card-text>
        <div class="font-weight-medium">
          {{ currentPublisher.name }}
        </div>
        <div
          v-if="currentPublisher.description"
          class="text--secondary mt-1"
        >
          {{ currentPublisher.description }}
        </div>
        <small
          v-if="showSuccess"
          class="green--text"
        >
          {{
            $t(
              "admin.productsDatabase.importWizard.steps.publicationDetails.publisherUpdated"
            )
          }}
        </small>
      </v-card-text>
    </v-card>

    <v-alert
      v-else
      type="info"
      text
      class="mt-4"
    >
      {{
        $t(
          "admin.productsDatabase.importWizard.steps.publicationDetails.noPublisherSelected"
        )
      }}
    </v-alert>
  </div>
</template>

<script>
import { debounce } from "lodash";
import PublisherQueries from "@/graphql/PublisherQueries";
import ProductInfoFieldMixin from "@/mixins/ProductInfoFieldMixin";

export default {
  name: "PublisherSelector",
  mixins: [ProductInfoFieldMixin],
  props: {
    initialPublisher: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      publisherSearch: "",
      isSearching: false,
      publisherResults: [],
      selectedPublisher: null,
      searchDebounced: null,
    };
  },
  computed: {
    currentPublisher() {
      return this.backendValue || null;
    },
  },
  watch: {
    publisherSearch(val) {
      if (val) {
        this.isSearching = true;
        this.searchDebounced(val);
      } else {
        this.publisherResults = [];
      }
    },
  },
  created() {
    this.searchDebounced = debounce(this.performPublisherSearch, 500);
  },
  methods: {
    async performPublisherSearch(searchTerm) {
      try {
        const result = await this.$apollo.query({
          query: PublisherQueries.getPublishers,
          variables: {
            first: 20,
            name: searchTerm,
          },
        });

        if (
          result.data &&
          result.data.publishers &&
          result.data.publishers.edges
        ) {
          this.publisherResults = result.data.publishers.edges.map(
            (edge) => edge.node
          );
        }
      } catch (error) {
        console.error("Error searching publishers:", error);
        this.$store.dispatch("snackbar/showSnackbar", {
          text: "Failed to search publishers",
          color: "error",
        });
      } finally {
        this.isSearching = false;
      }
    },

    updateBookPublisher(publisher) {
      if (!publisher) {
        this.selectedPublisher = null;
        return;
      }

      // Use ProductInfoFieldMixin to save the publisher ID
      // The GraphQL response should return the full publisher object
      this.onInput(publisher.id);

      // Clear the selection
      this.selectedPublisher = null;
      this.publisherSearch = "";
    },
  },
};
</script>
